import { useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@chakra-ui/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import {
  countRecipientsThatFit,
  type RecipientLineWidths,
} from './recipientLineFit';
import {
  recipientLabel,
  summarizeRecipients,
  type ConversationRecipient,
} from './conversationRecipients';

interface RecipientSummaryTextProps {
  /** "CC:" / "BCC:" — the copy that introduces the list. */
  label?: string;
  recipients?: ConversationRecipient[];
  /**
   * A fixed number of names to print before counting the rest.
   *
   * Omit it to fill the line instead: the list then prints as many names as the
   * row is wide enough for. The sentence at the top of the header takes a limit
   * because it shares its line with the rest of a sentence; the CC and BCC lines
   * have a line to themselves and measure.
   */
  limit?: number;
  testId?: string;
}

const moreLabel = (count: number) => `+${count} more`;

/** Marks the parts the fitting maths reads, so it can find them in one query. */
const LEAD = 'data-summary-lead';
const ITEM = 'data-summary-item';
const SIZER = 'data-summary-sizer';

const readWidths = (row: HTMLElement): RecipientLineWidths => ({
  lead: row.querySelector<HTMLElement>(`[${LEAD}]`)?.offsetWidth ?? 0,
  // `Array.from`, not a spread: this repo targets es5, where spreading a
  // NodeList is a type error.
  items: Array.from(row.querySelectorAll<HTMLElement>(`[${ITEM}]`)).map(
    (item) => item.offsetWidth
  ),
  more: row.querySelector<HTMLElement>(`[${SIZER}]`)?.offsetWidth ?? 0,
});

/**
 * A recipient list on its own line in the conversation header.
 *
 * Prints the names the line has room for and counts the rest behind a hover, so
 * an email copying fifteen teammates still reads as one line about a message
 * rather than four lines of names. Nothing is lost — the tooltip lists every
 * hidden name.
 *
 * `@kula-ai/components` has no primitive for this (AvatarGroup counts avatars,
 * not text), so the fitting is local. It looks like it belongs upstream.
 */
export const RecipientSummaryText = ({
  label,
  recipients = [],
  limit,
  testId,
}: RecipientSummaryTextProps) => {
  const measures = limit === undefined;
  const { all } = summarizeRecipients(recipients, limit);

  const rowRef = useRef<HTMLDivElement>(null);
  /**
   * Taken from the one render where every name is on screen, and kept.
   *
   * A name's width does not change when the drawer does — only how many of them
   * the row has room for — so a resize is the arithmetic again, not another
   * pass through the DOM with all the names put back.
   */
  const widthsRef = useRef<RecipientLineWidths | null>(null);
  const [measured, setMeasured] = useState<{
    names: string;
    fits: number;
  } | null>(null);

  // The names themselves, not the array identity, which is rebuilt every render.
  const namesKey = all.map(recipientLabel).join('\u0000');

  /**
   * `null` means "not measured yet", and the render that says so is the one
   * showing every name — which is exactly the render there is something to
   * measure.
   *
   * Derived against the names rather than reset when they change: a new list is
   * new text, so an answer about the old one is not an answer at all, and
   * reading it this way puts every name back on screen without an effect whose
   * only job is to undo state.
   */
  const fits = measured?.names === namesKey ? measured.fits : null;

  // Runs only on the all-shown render. Measuring a row that is already cut
  // would size the list against the names it had last time.
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!measures || fits !== null || !row) return;

    widthsRef.current = readWidths(row);
    setMeasured({
      names: namesKey,
      fits: countRecipientsThatFit(widthsRef.current, row.clientWidth),
    });
  }, [measures, fits, namesKey]);

  // The drawer is resizable, and the same list fits differently at each width.
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!measures || !row) return undefined;

    const observer = new ResizeObserver(() => {
      if (!widthsRef.current) return;
      setMeasured({
        names: namesKey,
        fits: countRecipientsThatFit(widthsRef.current, row.clientWidth),
      });
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, [measures, namesKey]);

  if (all.length === 0) return null;

  const shown = measures
    ? all.slice(0, fits ?? all.length)
    : all.slice(0, limit);
  const hidden = all.slice(shown.length);
  const hiddenNames = hidden.map(recipientLabel).join(', ');

  const more = hidden.length > 0 && (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        {/* Chakra's Box, not a text component: `asChild` hands the trigger its
            ref, and the line around it already owns the type. */}
        <TooltipTrigger asChild>
          <Box
            as="span"
            // The one word on the line darker than the names it stands for —
            // the part that answers on hover rather than on sight.
            color="secondary-800"
            textDecoration="underline"
            textDecorationStyle="dotted"
            cursor="default"
            whiteSpace="nowrap"
            aria-label={hiddenNames}
            data-test-id={testId ? `${testId}-more` : undefined}
          >
            {moreLabel(hidden.length)}
          </Box>
        </TooltipTrigger>
        <TooltipContent side="top" collisionPadding={12}>
          {hiddenNames}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  return (
    <Box
      ref={rowRef}
      as="span"
      /**
       * A box of its own only where it owns the line.
       *
       * The CC and BCC lines need one: an inline box reports no width, so there
       * would be nothing to fit the names against, and the cut has to clip
       * whatever the last name overshoots by. The To clause is a phrase inside
       * the header's sentence — give that a block and the sentence breaks
       * around it, stranding the recipient and the timestamp on lines of their
       * own.
       */
      {...(measures
        ? { display: 'block', whiteSpace: 'nowrap', overflow: 'hidden' }
        : {})}
      data-test-id={testId}
    >
      {label ? <Box as="span" {...{ [LEAD]: true }}>{`${label} `}</Box> : null}

      {shown.map((recipient, index) => (
        <Box
          as="span"
          /**
           * Only on the lines this component owns.
           *
           * There the names are darkened against the label that introduces
           * them. In the header's sentence they are one phrase among several
           * that the sentence has already styled, and setting a colour and a
           * weight here overrode it.
           */
          {...(measures ? { color: 'secondary-800', fontWeight: 600 } : {})}
          key={`${recipientLabel(recipient)}-${index}`}
          {...{ [ITEM]: true }}
        >
          {recipientLabel(recipient)}
          {index < shown.length - 1 ? ', ' : ''}
        </Box>
      ))}

      {more ? <> {more}</> : null}

      {/* Off-screen, and only while measuring: the fitting has to know how much
          room "+N more" wants before it knows whether it needs one. */}
      {measures && fits === null && all.length > 0 ? (
        <Box
          as="span"
          position="absolute"
          visibility="hidden"
          aria-hidden="true"
          {...{ [SIZER]: true }}
        >
          {moreLabel(all.length)}
        </Box>
      ) : null}
    </Box>
  );
};
